import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';

function getNextQuoteName(existingNames) {
    const nums = existingNames
        .map(n => { const m = n.match(/^Quote (\d+):/); return m ? parseInt(m[1], 10) : 0; })
        .filter(n => n > 0);
    const next = nums.length > 0 ? Math.max(...nums) + 1 : 1;
    return `Quote ${next}: `;
}

export default function AddQuoteDialog({ open, onClose, onSubmit, existingNames = [], initialName = '', quoteId = '' }) {
    const [name, setName] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setName(initialName || getNextQuoteName(existingNames));
            setError('');
        }
    }, [open, initialName, existingNames]);

    function validate(value) {
        const trimmed = value.trim();
        if (!trimmed) {
            return 'Quote name is required.';
        }
        if (existingNames.includes(trimmed) && trimmed !== initialName.trim()) {
            return 'A quote with this name already exists.';
        }
        return '';
    }

    function handleChange(e) {
        const value = e.target.value;
        setName(value);
        setError(validate(value));
    }

    async function handleSubmit(e) {
        e.preventDefault();
        const validationError = validate(name);
        if (validationError) {
            setError(validationError);
            return;
        }
        setLoading(true);
        try {
            await onSubmit({ name: name.trim(), id: quoteId });
            onClose();
        } catch (err) {
            console.error('Failed to add quote:', err);
        } finally {
            setLoading(false);
        }
    }

    const isRename = Boolean(initialName);
    const dialogTitle = isRename ? 'Rename Quote' : 'Add Quote';
    const submitLabel = isRename ? 'Save' : 'Add Quote';

    return (
        <Dialog
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 7,
                    py: 1.5,
                    px: 1.5,
                    backgroundColor: '#f1f4fa',
                },
            }}
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            TransitionProps={{
                onEntered: () => {
                    if (inputRef.current) {
                        inputRef.current.focus();
                        const len = inputRef.current.value.length;
                        inputRef.current.setSelectionRange(len, len);
                    }
                },
            }}
        >
            <form onSubmit={handleSubmit}>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>
                    <TextField
                        inputRef={inputRef}
                        margin="dense"
                        label="Quote Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={name}
                        onChange={handleChange}
                        error={Boolean(error)}
                        helperText={error}
                        disabled={loading}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || !name.trim() || Boolean(error)}
                        sx={{ backgroundColor: '#673ab7', '&:hover': { backgroundColor: '#512da8' } }}
                    >
                        {loading ? (isRename ? 'Saving...' : 'Adding...') : submitLabel}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
